// Phase Q.3 — Offline sales outbox.
//
// When createSale fails with a network error, the payload + a client-
// issued ULID idempotency key are persisted here. On reconnect the
// drainer replays each entry through the same createSale endpoint.
// The server uses the idempotencyKey as the Sale.Id, and its X.3 atomic
// batch turns a duplicate insert into "already recorded; return the
// existing sale," so a retry mid-drain doesn't double-record.
//
// Entries are scoped per shop via posDb.shopDb(shopName) so switching
// shops never replays one shop's queued sales against another's
// endpoint. Storage lives in the existing keyValueUpload store
// (designed for exactly this kind of replay-on-reconnect queue, see
// database.ts:34).

import { posDb, stores } from './database';
import type { ICreateSaleRequest } from '../api/model/requests';

// ULID-shaped client id. Matches the server's ^[0-9A-HJKMNP-TV-Z]{26}$
// gate. Generated entirely client-side so we can write the outbox row
// before the network attempt fires.
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export function newSaleIdempotencyKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  // Time portion: 10 chars, Crockford base32 of milliseconds-since-epoch.
  let time = '';
  let ms = Date.now();
  for (let i = 9; i >= 0; i--) {
    time = CROCKFORD[ms % 32] + time;
    ms = Math.floor(ms / 32);
  }
  // Random portion: 16 chars from getRandomValues.
  let rand = '';
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    for (let i = 0; i < 16; i++) rand += CROCKFORD[bytes[i] % 32];
  } else {
    // Fallback only — getRandomValues is universally available in any
    // browser that runs the PWA.
    for (let i = 0; i < 16; i++)
      rand += CROCKFORD[Math.floor(Math.random() * 32)];
  }
  return time + rand;
}

export type PendingSaleStatus = 'queued' | 'replaying' | 'failed';

export interface PendingSale {
  // The ULID we hand the server as Sale.Id when the entry replays.
  idempotencyKey: string;
  // The original cashier-side payload, unchanged from what createSale
  // was called with.
  payload: ICreateSaleRequest;
  // ISO 8601 UTC timestamp of when the entry was queued (cart was
  // rung up offline). Used to FIFO the drain in submission order.
  queuedAtUtc: string;
  // Current replay state. 'queued' = waiting; 'replaying' = drainer
  // is mid-flight; 'failed' = server returned a non-network rejection
  // (e.g. menu item gone). Failed entries surface to the operator
  // until they dismiss.
  status: PendingSaleStatus;
  // When status is 'failed', the most recent server message.
  lastError?: string;
}

// Outbox entries are stored in the existing keyValueUpload store under
// id `sale-outbox:{idempotencyKey}`. That store was originally provisioned
// for offline upload replay (database.ts) — this is its first real
// consumer.
const ID_PREFIX = 'sale-outbox:';
function rowId(idempotencyKey: string): string {
  return `${ID_PREFIX}${idempotencyKey}`;
}

// Enqueue a pending sale. Returns the idempotency key the server will
// use as Sale.Id. Writes synchronously to IDB so the row is durable
// before any network attempt that might fail.
export async function enqueueSale(
  shopName: string,
  payload: ICreateSaleRequest
): Promise<PendingSale> {
  const key = newSaleIdempotencyKey();
  const entry: PendingSale = {
    idempotencyKey: key,
    payload: { ...payload, idempotencyKey: key },
    queuedAtUtc: new Date().toISOString(),
    status: 'queued',
  };
  await posDb.shopDb(shopName).setRecord(rowId(key), entry, {
    store: stores.keyValueUpload,
    uploadAction: {
      apiUrl: `/api/shop/${encodeURIComponent(shopName)}/sales`,
      body: entry.payload as unknown as object,
    },
  });

  // READ IT BACK before telling the caller the sale is safely queued.
  //
  // Not defensive padding: `put()` in indexed-db-wrapper.ts catches every failure and returns
  // false, and `setRecord()` discards that boolean, so a write that did not happen is
  // indistinguishable from one that did. When the upload store was missing entirely (fixed in
  // schema version 2) that silence turned into the worst outcome a till can have - createSale
  // returned success, the customer was shown their confirmation, and the sale existed nowhere.
  //
  // The caller depends on this throwing: createSale's catch surfaces the original network error
  // when enqueueing fails, which is what puts the failure in front of the cashier while the
  // customer is still standing there. Anything cheaper than a read-back would leave that promise
  // resting on a swallowed exception again.
  const queued = await listPendingSales(shopName);
  if (!queued.some((q) => q.idempotencyKey === key)) {
    throw new Error(
      'The sale could not be queued for later - this device cannot store it offline.'
    );
  }
  return entry;
}

// List every pending sale for the shop in FIFO submission order.
export async function listPendingSales(
  shopName: string
): Promise<PendingSale[]> {
  const rows = await posDb.shopDb(shopName).getUploadRecords();
  const ours: PendingSale[] = [];
  for (const row of rows) {
    if (!row.id.startsWith(ID_PREFIX)) continue;
    const value = row.value as PendingSale | undefined;
    if (!value || typeof value !== 'object') continue;
    if (typeof value.idempotencyKey !== 'string') continue;
    ours.push(value);
  }
  // Submission order = queuedAtUtc ascending. Ties broken by key (stable).
  ours.sort((a, b) => {
    if (a.queuedAtUtc < b.queuedAtUtc) return -1;
    if (a.queuedAtUtc > b.queuedAtUtc) return 1;
    return a.idempotencyKey < b.idempotencyKey ? -1 : 1;
  });
  return ours;
}

export async function updatePendingSale(
  shopName: string,
  entry: PendingSale
): Promise<void> {
  await posDb.shopDb(shopName).setRecord(rowId(entry.idempotencyKey), entry, {
    store: stores.keyValueUpload,
    uploadAction: {
      apiUrl: `/api/shop/${encodeURIComponent(shopName)}/sales`,
      body: entry.payload as unknown as object,
    },
  });
}

export async function deletePendingSale(
  shopName: string,
  idempotencyKey: string
): Promise<void> {
  await posDb.shopDb(shopName).deleteRecordFromUpload(rowId(idempotencyKey));
}

// Count helper for the UI pill. Cheap — scans keyValueUpload but the
// store is tiny.
export async function countPendingSales(shopName: string): Promise<number> {
  const list = await listPendingSales(shopName);
  return list.length;
}
